import {
  useState,
} from 'react';

import type {
  SystemScenarioRunRecord,
  SystemWorkspaceDefinition,
} from '@/governor/system-workspaces/SystemWorkspaceContracts';

import {
  projectMembershipBillingWorkspace,
} from '@/governor/system-workspaces/MembershipBillingWorkspaceProjection';

import {
  runAllMembershipBillingScenarios,
  runMembershipBillingScenario,
} from '@/governor/system-workspaces/MembershipBillingSimulatorRuntime';

import type {
  MembershipScenarioId,
} from '@/governor/system-workspaces/MembershipBillingScenarioPackage';

function stateClass(
  state: string,
): string {
  if (
    state === 'pass' ||
    state === 'Healthy'
  ) {
    return 'border-emerald-200 bg-emerald-50 text-emerald-900';
  }

  if (
    state === 'fail' ||
    state === 'Blocked'
  ) {
    return 'border-rose-200 bg-rose-50 text-rose-900';
  }

  if (
    state === 'pending' ||
    state === 'not_run' ||
    state === 'Degraded'
  ) {
    return 'border-orange-200 bg-orange-50 text-orange-900';
  }

  return 'border-slate-200 bg-slate-50 text-slate-800';
}

export default function MembershipBillingOperationsWorkspace({
  workspace,
}: {
  workspace: SystemWorkspaceDefinition;
}) {
  const [
    refreshVersion,
    setRefreshVersion,
  ] = useState(0);

  const [
    runningScenarioId,
    setRunningScenarioId,
  ] = useState<string | null>(null);

  const [
    lastRunMessage,
    setLastRunMessage,
  ] = useState<string | null>(null);

  const projection =
    projectMembershipBillingWorkspace(
      workspace,
    );

  void refreshVersion;

  const runScenario = async (
    scenarioId: MembershipScenarioId,
  ) => {
    setRunningScenarioId(scenarioId);
    setLastRunMessage(null);

    try {
      const result =
        await runMembershipBillingScenario(
          scenarioId,
        );

      setLastRunMessage(
        `${result.scenarioId}: ${
          result.passed
            ? 'PASS'
            : 'FAIL'
        }`,
      );
    } catch (error) {
      setLastRunMessage(
        error instanceof Error
          ? error.message
          : String(error),
      );
    } finally {
      setRunningScenarioId(null);
      setRefreshVersion(
        value => value + 1,
      );
    }
  };

  const runAll = async () => {
    setRunningScenarioId('all');
    setLastRunMessage(null);

    try {
      const results =
        await runAllMembershipBillingScenarios();

      const passed =
        results.filter(
          result => result.passed,
        ).length;

      setLastRunMessage(
        `Membership simulation package: ${passed}/${results.length} passed.`,
      );
    } catch (error) {
      setLastRunMessage(
        error instanceof Error
          ? error.message
          : String(error),
      );
    } finally {
      setRunningScenarioId(null);
      setRefreshVersion(
        value => value + 1,
      );
    }
  };

  const latestRunFor = (
    scenarioId: string,
  ): SystemScenarioRunRecord | null =>
    projection.simulator.runs.find(
      run =>
        run.scenarioId ===
        scenarioId,
    ) ?? null;

  return (
    <div className="mt-5 space-y-5">
      <section className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-violet-700">
              Membership & Billing operations
            </p>

            <h4 className="mt-2 text-2xl font-bold text-slate-950">
              Governed Runtime Workspace
            </h4>
          </div>

          <div
            className={`rounded-full border px-3 py-1 text-xs font-bold uppercase ${stateClass(
              projection.status.label,
            )}`}
          >
            {projection.status.label}
          </div>
        </div>

        <div className="mt-4 rounded-xl border border-orange-200 bg-orange-50 p-4 text-sm text-orange-900">
          {projection.status.reason}
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-5">
        <h4 className="text-lg font-bold text-slate-950">
          Runtime Evidence
        </h4>

        <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-xl bg-slate-50 p-3">
            <p className="text-xs font-bold uppercase text-slate-500">
              Evidence records
            </p>
            <p className="mt-1 text-2xl font-bold">
              {projection.evidence.records.length}
            </p>
          </div>

          <div className="rounded-xl bg-slate-50 p-3">
            <p className="text-xs font-bold uppercase text-slate-500">
              Historical
            </p>
            <p className="mt-1 text-2xl font-bold">
              {
                projection.evidence.records.filter(
                  record =>
                    record.mode ===
                    'HISTORICAL',
                ).length
              }
            </p>
          </div>

          <div className="rounded-xl bg-slate-50 p-3">
            <p className="text-xs font-bold uppercase text-slate-500">
              Simulated
            </p>
            <p className="mt-1 text-2xl font-bold">
              {
                projection.evidence
                  .simulatedEvidenceCount
              }
            </p>
          </div>

          <div className="rounded-xl bg-slate-50 p-3">
            <p className="text-xs font-bold uppercase text-slate-500">
              Live
            </p>
            <p className="mt-1 text-2xl font-bold">
              {
                projection.evidence
                  .liveEvidenceCount
              }
            </p>
          </div>
        </div>

        <div className="mt-4 space-y-3">
          {projection.evidence.records.map(
            record => (
              <div
                key={record.id}
                className="rounded-xl border border-slate-200 bg-slate-50 p-4"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="font-bold text-slate-900">
                    {record.source}
                  </p>

                  <span className="rounded-full bg-slate-200 px-2 py-1 text-[0.65rem] font-bold uppercase text-slate-700">
                    {record.mode}
                  </span>
                </div>

                <p className="mt-2 text-sm text-slate-600">
                  {record.summary}
                </p>
              </div>
            ),
          )}
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-5">
        <h4 className="text-lg font-bold text-slate-950">
          Certification Matrix
        </h4>

        <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {Object.entries(
            projection.certification
              .certification,
          ).map(([key, value]) => (
            <div
              key={key}
              className={`rounded-xl border p-3 ${stateClass(
                value,
              )}`}
            >
              <p className="text-xs font-bold uppercase">
                {key.replaceAll(
                  /([A-Z])/g,
                  ' $1',
                )}
              </p>

              <p className="mt-1 text-lg font-bold uppercase">
                {value.replaceAll('_', ' ')}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-2xl border border-sky-200 bg-sky-50 p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h4 className="text-lg font-bold text-sky-950">
              Controlled Membership Simulator
            </h4>

            <p className="mt-2 text-sm text-sky-900">
              {
                projection.simulator
                  .passedRequiredCount
              }
              /
              {
                projection.simulator
                  .requiredScenarioCount
              }{' '}
              required scenarios currently pass.
            </p>
          </div>

          <button
            type="button"
            disabled={
              runningScenarioId !== null
            }
            onClick={() => {
              void runAll();
            }}
            className="rounded-xl bg-sky-700 px-4 py-2 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            {runningScenarioId === 'all'
              ? 'Running package...'
              : 'Run all simulations'}
          </button>
        </div>

        {lastRunMessage ? (
          <div className="mt-4 rounded-xl border border-sky-200 bg-white p-3 text-sm font-bold text-sky-950">
            {lastRunMessage}
          </div>
        ) : null}

        <div className="mt-4 space-y-3">
          {projection.simulator.scenarios.map(
            scenario => {
              const latest =
                latestRunFor(
                  scenario.id,
                );

              return (
                <div
                  key={scenario.id}
                  className="rounded-xl border border-sky-200 bg-white p-4"
                >
                  <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                    <div>
                      <p className="font-bold text-slate-950">
                        {scenario.name}
                      </p>

                      <p className="mt-1 text-sm text-slate-600">
                        {scenario.description}
                      </p>

                      <p className="mt-2 text-xs font-bold uppercase text-sky-700">
                        {latest
                          ? `Latest: ${latest.state}`
                          : 'Not run'}
                      </p>
                    </div>

                    <button
                      type="button"
                      disabled={
                        runningScenarioId !==
                        null
                      }
                      onClick={() => {
                        void runScenario(
                          scenario.id as MembershipScenarioId,
                        );
                      }}
                      className="rounded-xl border border-sky-300 px-3 py-2 text-sm font-bold text-sky-900 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {runningScenarioId ===
                      scenario.id
                        ? 'Running...'
                        : 'Run scenario'}
                    </button>
                  </div>
                </div>
              );
            },
          )}
        </div>

        <div className="mt-4 rounded-xl border border-sky-200 bg-white/80 p-3 text-sm text-sky-950">
          Production writes are blocked. Every run is isolated,
          namespaced, reset after execution, and retained only in
          the in-memory simulator evidence store.
        </div>
      </section>

      <div className="grid gap-5 xl:grid-cols-2">
        <section className="rounded-2xl border border-violet-200 bg-violet-50 p-5">
          <h4 className="text-lg font-bold text-violet-950">
            Governed AI Assistance
          </h4>

          <p className="mt-3 text-sm text-violet-900">
            Contract installed. Maximum Level{' '}
            {projection.ai.maximumLevel}.
            Production execution remains blocked.
          </p>

          <p className="mt-3 text-sm text-violet-900">
            Membership-specific AI registration is deferred until
            the simulation evidence package is reviewed.
          </p>
        </section>

        <section className="rounded-2xl border border-purple-200 bg-purple-50 p-5">
          <h4 className="text-lg font-bold text-purple-950">
            Incidents & Repair Readiness
          </h4>

          <p className="mt-3 text-sm text-purple-900">
            Simulation failures:{' '}
            <b>
              {projection.incidents.activeCount}
            </b>
          </p>

          <p className="mt-2 text-sm text-purple-900">
            Level 4 approved execution authority is installed, but
            no Membership repair handler is registered.
          </p>

          <p className="mt-2 text-sm text-purple-900">
            Level 5 autonomous repair remains disabled until an
            individual repair type earns autonomous certification.
          </p>
        </section>
      </div>
    </div>
  );
}
